import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Package, Image, Video, FileText, Plus, X, Check, 
  Layers, FolderOpen, ChevronDown, ChevronUp, Search,
  Save, RefreshCw, Eye, Download
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ALL product types that can have content - COMPLETE OVERVIEW
const PRODUCT_CATEGORIES = [
  {
    name: '📅 Subscriptions (Monthly)',
    icon: '📅',
    products: [
      { id: 'daily_tease', name: 'Daily Tease', price: '€4.28/month', color: '#D4AF37' },
      { id: 'midnight_crave', name: 'Midnight Crave', price: '€9.28/month', color: '#C0C0C0' },
      { id: 'pure_obsession', name: 'Pure Obsession', price: '€14.28/month', color: '#FFD700' },
      { id: 'gold_vip', name: 'Gold VIP', price: '€28.28/month', color: '#D4AF37' },
    ]
  },
  {
    name: '📦 One-Time Packages',
    icon: '📦',
    products: [
      { id: 'bronze', name: 'Bronze Package', price: '€5 - 5 photos', color: '#CD7F32' },
      { id: 'silver', name: 'Silver Package', price: '€10 - 10 photos + 2 videos', color: '#C0C0C0' },
      { id: 'gold', name: 'Gold Package', price: '€15 - 20 photos + 5 videos', color: '#FFD700' },
      { id: 'platinum', name: 'Platinum Package', price: '€20 - 50 photos + 10 videos', color: '#E5E4E2' },
      { id: 'lifetime', name: 'Lifetime Access', price: '€80 - Unlimited', color: '#9B59B6' },
    ]
  },
  {
    name: '🔥 PPV Content (Pay Per View)',
    icon: '🔥',
    products: [
      { id: 'ppv_basic', name: 'Basic PPV', price: '€4.99', color: '#E74C3C' },
      { id: 'ppv_premium', name: 'Premium PPV', price: '€9.99', color: '#E74C3C' },
      { id: 'ppv_ultra', name: 'Ultra PPV', price: '€19.99', color: '#E74C3C' },
      { id: 'ppv_exclusive_1', name: 'Exclusive PPV 1', price: 'Variable', color: '#FF6B6B' },
      { id: 'ppv_exclusive_2', name: 'Exclusive PPV 2', price: 'Variable', color: '#FF6B6B' },
      { id: 'ppv_exclusive_3', name: 'Exclusive PPV 3', price: 'Variable', color: '#FF6B6B' },
    ]
  },
  {
    name: '🎨 Custom Art Builder',
    icon: '🎨',
    products: [
      { id: 'basic_build', name: 'Basic Build', price: '15 tokens', color: '#9B59B6' },
      { id: 'premium_craft', name: 'Premium Craft', price: '30 tokens', color: '#9B59B6' },
      { id: 'vip_masterpiece', name: 'VIP Masterpiece', price: '50 tokens', color: '#9B59B6' },
    ]
  },
  {
    name: '🛒 Shop Products',
    icon: '🛒',
    products: [
      { id: 'prompt_guide', name: 'Ultimate Prompt Guide', price: '€49 - PDF', color: '#3498DB' },
      { id: 'hd_pack_10', name: '10 HD Art Pack', price: '€9.99', color: '#3498DB' },
      { id: 'hd_pack_50', name: '50 HD Art Pack', price: '€29.99', color: '#3498DB' },
      { id: 'video_pack', name: 'Motion Art Collection', price: '€19.99', color: '#E74C3C' },
      { id: 'wallpaper_pack', name: 'Digital Wallpapers', price: '€4.99', color: '#2ECC71' },
      { id: '4k_collection', name: '4K Art Collection', price: '€39.99', color: '#F39C12' },
      { id: 'calendar_2025', name: '2025 Art Calendar', price: '€24.99', color: '#1ABC9C' },
      { id: 'poster_set', name: 'Art Print Set', price: '€34.99', color: '#E91E63' },
      { id: 'phone_case', name: 'Custom Art Phone Case', price: '€29.99', color: '#9C27B0' },
      { id: 'video_message', name: 'Custom Animation', price: '€49.99', color: '#FF5722' },
      { id: 'custom_set', name: 'Custom Art Set', price: '€24.99', color: '#795548' },
      { id: 'ambient_pack', name: 'Ambient Music Pack', price: '€14.99', color: '#607D8B' },
      { id: 'meditation', name: 'Meditation Audio', price: '€9.99', color: '#00BCD4' },
    ]
  },
  {
    name: '🎁 Special Bundles',
    icon: '🎁',
    products: [
      { id: 'starter_pack', name: 'Starter Pack', price: '€20', color: '#1ABC9C' },
      { id: 'ultimate_bundle', name: 'Ultimate Bundle', price: '80 tokens', color: '#9B59B6' },
      { id: 'summer_edition', name: 'Summer Edition', price: '€25', color: '#F39C12' },
      { id: 'birthday_bundle', name: 'Birthday Bundle', price: '€30', color: '#E91E63' },
      { id: 'valentines_special', name: 'Valentines Special', price: '€25', color: '#E74C3C' },
      { id: 'premium_collection', name: 'Premium Collection', price: '€40', color: '#9C27B0' },
    ]
  },
  {
    name: '🤖 AI & Specials',
    icon: '🤖',
    products: [
      { id: 'ai_girlfriend', name: 'Custom AI Girlfriend', price: '€350', color: '#FF4081' },
    ]
  }
];

export default function ContentManager() {
  const [allMedia, setAllMedia] = useState([]);
  const [productContent, setProductContent] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [selectMediaDialog, setSelectMediaDialog] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedCategories, setExpandedCategories] = useState([
    '📅 Subscriptions (Monthly)', 
    '📦 One-Time Packages',
    '🔥 PPV Content (Pay Per View)',
    '🎨 Custom Art Builder',
    '🛒 Shop Products',
    '🎁 Special Bundles',
    '🤖 AI & Specials'
  ]);
  const [syncing, setSyncing] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      // Haal alle media op
      const mediaRes = await axios.get(`${API}/admin/media`, { headers });
      setAllMedia(mediaRes.data || []);

      // Haal content per product op
      const foldersRes = await axios.get(`${API}/admin/folders`, { headers });
      const folderData = {};
      foldersRes.data.forEach(f => {
        folderData[f.folder] = f.media_count;
      });

      // Haal gedetailleerde content per product
      const contentMap = {};
      for (const category of PRODUCT_CATEGORIES) {
        for (const product of category.products) {
          try {
            const productMedia = mediaRes.data.filter(m => m.folder === product.id);
            contentMap[product.id] = {
              count: productMedia.length,
              items: productMedia
            };
          } catch {
            contentMap[product.id] = { count: 0, items: [] };
          }
        }
      }
      setProductContent(contentMap);

    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const toggleCategory = (categoryName) => {
    setExpandedCategories(prev => 
      prev.includes(categoryName) 
        ? prev.filter(c => c !== categoryName)
        : [...prev, categoryName]
    );
  };

  const openMediaSelector = (product) => {
    setSelectedProduct(product);
    setSelectMediaDialog(true);
  };

  const assignMediaToProduct = async (mediaId) => {
    if (!selectedProduct) return;

    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API}/admin/media/${mediaId}/assign-folder?folder=${selectedProduct.id}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(`Media added to ${selectedProduct.name}`);
      fetchData(); // Refresh data
    } catch (error) {
      toast.error('Failed to assign media');
    }
  };

  const removeMediaFromProduct = async (mediaId, productId) => {
    try {
      const token = localStorage.getItem('token');
      // Move back to gallery
      await axios.put(
        `${API}/admin/media/${mediaId}/assign-folder?folder=gallery`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Media removed from product');
      fetchData();
    } catch (error) {
      toast.error('Failed to remove media');
    }
  };

  const syncAllMembers = async () => {
    try {
      setSyncing(true);
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      // Sync content access for all users
      const res = await axios.post(`${API}/admin/sync-content-access`, {}, { headers });
      toast.success(res.data.message || 'Content synced to all members!');
    } catch (error) {
      toast.error('Sync failed');
    } finally {
      setSyncing(false);
    }
  };

  const getMediaIcon = (media) => {
    if (media.content_type?.includes('video')) return <Video className="w-4 h-4" />;
    if (media.content_type?.includes('pdf')) return <FileText className="w-4 h-4" />;
    return <Image className="w-4 h-4" />;
  };

  const filteredMedia = allMedia.filter(m => 
    (m.folder === 'gallery' || !m.folder) &&
    (searchTerm === '' || m.filename?.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="w-8 h-8 text-[#D4AF37] animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header met uitleg */}
      <div className="glass rounded-xl p-6">
        <div className="flex items-start justify-between">
          <div>
            <h2 className="text-xl font-bold flex items-center gap-2">
              <Layers className="w-6 h-6 text-[#D4AF37]" />
              Content Manager
            </h2>
            <p className="text-gray-400 mt-2 max-w-2xl">
              Assign photos, videos and PDFs to products. Members automatically receive 
              access to the content after purchase. Click on a product to add media.
            </p>
          </div>
          <Button 
            onClick={syncAllMembers}
            disabled={syncing}
            className="btn-gold"
          >
            {syncing ? (
              <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <RefreshCw className="w-4 h-4 mr-2" />
            )}
            Sync to Members
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
          <div className="bg-white/5 rounded-lg p-4 text-center">
            <div className="text-2xl font-bold text-[#D4AF37]">{allMedia.length}</div>
            <div className="text-xs text-gray-400">Total Media</div>
          </div>
          <div className="bg-white/5 rounded-lg p-4 text-center">
            <div className="text-2xl font-bold text-green-400">
              {filteredMedia.length}
            </div>
            <div className="text-xs text-gray-400">In Gallery (unassigned)</div>
          </div>
          <div className="bg-white/5 rounded-lg p-4 text-center">
            <div className="text-2xl font-bold text-blue-400">
              {allMedia.filter(m => m.folder && m.folder !== 'gallery').length}
            </div>
            <div className="text-xs text-gray-400">Assigned to Products</div>
          </div>
          <div className="bg-white/5 rounded-lg p-4 text-center">
            <div className="text-2xl font-bold text-purple-400">
              {PRODUCT_CATEGORIES.reduce((acc, c) => acc + c.products.length, 0)}
            </div>
            <div className="text-xs text-gray-400">Total Products</div>
          </div>
        </div>
      </div>

      {/* Product Categorieën */}
      <div className="space-y-4">
        {PRODUCT_CATEGORIES.map(category => (
          <div key={category.name} className="glass rounded-xl overflow-hidden">
            {/* Category Header */}
            <button
              onClick={() => toggleCategory(category.name)}
              className="w-full flex items-center justify-between p-4 hover:bg-white/5 transition-colors"
            >
              <div className="flex items-center gap-3">
                <span className="text-2xl">{category.icon}</span>
                <span className="font-medium text-lg">{category.name}</span>
                <span className="text-sm text-gray-400">
                  ({category.products.length} producten)
                </span>
              </div>
              {expandedCategories.includes(category.name) ? (
                <ChevronUp className="w-5 h-5 text-gray-400" />
              ) : (
                <ChevronDown className="w-5 h-5 text-gray-400" />
              )}
            </button>

            {/* Products Grid */}
            {expandedCategories.includes(category.name) && (
              <div className="p-4 pt-0 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {category.products.map(product => {
                  const content = productContent[product.id] || { count: 0, items: [] };
                  
                  return (
                    <div 
                      key={product.id}
                      className="bg-white/5 rounded-xl p-4 border border-white/10 hover:border-[#D4AF37]/50 transition-all group"
                    >
                      {/* Product Header */}
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <h4 className="font-medium" style={{ color: product.color }}>
                            {product.name}
                          </h4>
                          <p className="text-xs text-gray-400">{product.price}</p>
                        </div>
                        <div 
                          className="w-8 h-8 rounded-lg flex items-center justify-center text-sm font-bold"
                          style={{ backgroundColor: product.color + '20', color: product.color }}
                        >
                          {content.count}
                        </div>
                      </div>

                      {/* Content Preview */}
                      <div className="grid grid-cols-3 gap-1 mb-3 min-h-[60px]">
                        {content.items.slice(0, 6).map((item, idx) => (
                          <div 
                            key={item.id || idx}
                            className="aspect-square rounded overflow-hidden relative group/item"
                          >
                            {item.content_type?.includes('video') ? (
                              <div className="w-full h-full bg-black/50 flex items-center justify-center">
                                <Video className="w-4 h-4 text-white" />
                              </div>
                            ) : item.content_type?.includes('pdf') ? (
                              <div className="w-full h-full bg-red-500/20 flex items-center justify-center">
                                <FileText className="w-4 h-4 text-red-400" />
                              </div>
                            ) : (
                              <img 
                                src={`${API}/media/${item.id}`}
                                alt=""
                                className="w-full h-full object-cover"
                                onError={(e) => { e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><rect fill="%23333" width="24" height="24"/></svg>'; }}
                              />
                            )}
                            {/* Remove button */}
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                removeMediaFromProduct(item.id, product.id);
                              }}
                              className="absolute inset-0 bg-black/60 opacity-0 group-hover/item:opacity-100 transition-opacity flex items-center justify-center"
                            >
                              <X className="w-4 h-4 text-red-400" />
                            </button>
                          </div>
                        ))}
                        {content.count > 6 && (
                          <div className="aspect-square rounded bg-white/10 flex items-center justify-center text-xs text-gray-400">
                            +{content.count - 6}
                          </div>
                        )}
                        {content.count === 0 && (
                          <div className="col-span-3 flex items-center justify-center text-gray-500 text-xs">
                            No content
                          </div>
                        )}
                      </div>

                      {/* Add Content Button */}
                      <Button
                        onClick={() => openMediaSelector(product)}
                        variant="outline"
                        size="sm"
                        className="w-full border-dashed border-white/20 hover:border-[#D4AF37] hover:bg-[#D4AF37]/10"
                      >
                        <Plus className="w-4 h-4 mr-2" />
                        Add Content
                      </Button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Media Selector Dialog */}
      <Dialog open={selectMediaDialog} onOpenChange={setSelectMediaDialog}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-hidden flex flex-col bg-[#0a0a0a] border-white/10">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FolderOpen className="w-5 h-5 text-[#D4AF37]" />
              Selecteer Media voor{' '}
              <span style={{ color: selectedProduct?.color }}>
                {selectedProduct?.name}
              </span>
            </DialogTitle>
          </DialogHeader>

          {/* Search */}
          <div className="relative mb-4">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              placeholder="Search in gallery..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 bg-white/5 border-white/10"
            />
          </div>

          {/* Media Grid */}
          <div className="flex-1 overflow-y-auto">
            <p className="text-sm text-gray-400 mb-4">
              Click on an image to add it to {selectedProduct?.name}.
              Only media from the Gallery (unassigned) is shown.
            </p>
            
            {filteredMedia.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <FolderOpen className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>No media in Gallery</p>
                <p className="text-sm">First upload media via the "Upload Media" tab</p>
              </div>
            ) : (
              <div className="grid grid-cols-4 sm:grid-cols-6 gap-2">
                {filteredMedia.map(item => (
                  <button
                    key={item.id}
                    onClick={() => assignMediaToProduct(item.id)}
                    className="aspect-square rounded-lg overflow-hidden relative group hover:ring-2 hover:ring-[#D4AF37] transition-all"
                  >
                    {item.content_type?.includes('video') ? (
                      <div className="w-full h-full bg-black/50 flex items-center justify-center">
                        <Video className="w-6 h-6 text-white" />
                      </div>
                    ) : item.content_type?.includes('pdf') ? (
                      <div className="w-full h-full bg-red-500/20 flex items-center justify-center">
                        <FileText className="w-6 h-6 text-red-400" />
                      </div>
                    ) : (
                      <img 
                        src={`${API}/media/${item.id}`}
                        alt={item.filename}
                        className="w-full h-full object-cover"
                        onError={(e) => { e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><rect fill="%23333" width="24" height="24"/></svg>'; }}
                      />
                    )}
                    <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                      <Plus className="w-8 h-8 text-[#D4AF37]" />
                    </div>
                    <div className="absolute bottom-0 left-0 right-0 p-1 bg-gradient-to-t from-black/80">
                      <span className="text-[8px] text-gray-300 truncate block">
                        {item.filename}
                      </span>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex justify-between items-center pt-4 border-t border-white/10">
            <span className="text-sm text-gray-400">
              {filteredMedia.length} items available
            </span>
            <Button variant="outline" onClick={() => setSelectMediaDialog(false)}>
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
